import { BackendError, Service } from '@genial/services';
import {
  FundOfferBaseEntity,
  MetaResponse,
  ResponsesFundOfferCategoriesResponse,
} from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse, fakeMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { customerItemState } from '../../customer/atom';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  fundCategoriesFilterState,
  fundOffersAllState,
  fundOffersByFilterParamsState,
  fundOffersBySearchTermParamsState,
  fundOffersBySearchTermState,
  fundOffersOperationTypeState,
  fundRedemptionsOffersAllState,
  fundRedemptionsOffersBySearchTermParamsState,
  fundRedemptionsOffersBySearchTermState,
  productItemState,
} from '../atom';
import { fundComparisonPeriod, screenName } from '../constants';
import {
  normalizeFundComparison,
  normalizeFundOfferDetail,
  normalizeFundsFilters,
  normalizeFundsFiltersFromMeta,
  normalizeFundsOffers,
  normalizeFundsOffersV2,
  normalizeRedemptionsFund,
} from '../normalizer';
import {
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
  FundComparatorProductSearchResultNormalized,
  FundComparatorResultNormalized,
  FundOfferByFilterParams,
  FundOfferDetailEntityNormalized,
  FundOfferEntityNormalized,
  FundRedemptionOfferNormalized,
  FundsOffersBySearchTerm,
  type FundRedemptionsOffersBySearchTerm,
  type SelectedProduct,
} from '../types';

type FundCategoryQueryData = {
  data: ResponsesFundOfferCategoriesResponse[];
};

type FundOffersQueryData = {
  meta: MetaResponse;
  data: FundOfferBaseEntity[];
};

type FundDetailsQueryData = {
  data: FundOfferDetailEntityNormalized;
};
export const fundOffersBySearchTermParams = selector({
  key: 'fundOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentFundOffersBySearchTerm = get(fundOffersBySearchTermParamsState);
    const value = newValue as Partial<FundsOffersBySearchTerm>;

    const fundOffersBySearchTerm = {
      ...currentFundOffersBySearchTerm,
      ...value,
    };

    return set(fundOffersBySearchTermParamsState, fundOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(fundOffersBySearchTermParamsState);
    return item;
  },
});

export const fundOffersByFilterParams = selector({
  key: 'fundOffersByFilterParams',
  set: ({ set, get }, newValue) => {
    const currentFundOffersByFilter = get(fundOffersByFilterParamsState);
    const value = newValue as Partial<FundOfferByFilterParams>;

    const fundOffersByFilter = {
      ...currentFundOffersByFilter,
      ...value,
    };

    return set(fundOffersByFilterParamsState, fundOffersByFilter);
  },
  get: ({ get }) => {
    const item = get(fundOffersByFilterParamsState);
    return item;
  },
});

export const fundOffersQuery = selectorFamily<
  FundOffersQueryData | null | undefined,
  { checkingAccountNumber: number; accountNumber: number }
>({
  key: 'NewApplication/fundOffersQuery',
  get: ({ checkingAccountNumber, accountNumber }) => {
    return async ({ get }) => {
      try {
        const fundParams = get(fundOffersByFilterParamsState);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.Fund.V1.getOffersFilter({
          ...fundParams,
          checkingAccountNumber,
          accountNumber,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = data?.map((item) => item.offer) || [];
        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const fundOffersBySearchTermQuery = selectorFamily<
  FundOffersQueryData | null | undefined,
  { checkingAccountNumber: number; accountNumber: number }
>({
  key: 'NewApplication/fundOffersBySearchTermQuery',
  get: ({ checkingAccountNumber, accountNumber }) => {
    return async ({ get }) => {
      const fundParams = get(fundOffersBySearchTermParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      if (fundParams.skipEndpointCall) {
        return;
      }

      const { data, meta } = await Service.Fund.V1.getOffersFilter({
        ...fundParams,
        checkingAccountNumber,
        accountNumber,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = data?.map((item) => item.offer) || [];
      return { data: normalizedData, meta };
    };
  },
});

export const updateFundOffersAllSelector = selector({
  key: 'updateFundOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FundOfferBaseEntity[];
    return set(fundOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(fundOffersAllState);
    return item;
  },
});

export const updateFundOffersBySearchTermSelector = selector({
  key: 'updateFundOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FundOfferBaseEntity[];
    return set(fundOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(fundOffersBySearchTermState);
    return item;
  },
});

export const fundCategoriesQuery = selectorFamily<
  FundCategoryQueryData | null | undefined,
  { checkingAccountNumber: number; skipEndpointCall?: boolean }
>({
  key: 'NewApplication/fundCategoriesQuery',
  get: ({ checkingAccountNumber, skipEndpointCall }) => {
    return async ({ get }) => {
      try {
        if (skipEndpointCall) {
          const fundCategoriesFilter = get(fundCategoriesFilterState);
          return fundCategoriesFilter;
        }

        const { data } = await Service.Fund.V1.getOffersCategories({
          checkingAccountNumber,
          screenName,
        });

        const normalizedData = data?.map((item) => item) || [];
        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const updateFundCategoriesOptionsFilterSelector = selector({
  key: 'updateFundCategoriesOptionsFilterSelector',
  set: ({ set }, newValue) => {
    const value = { data: newValue } as { data: ResponsesFundOfferCategoriesResponse[] };
    return set(fundCategoriesFilterState, value);
  },
  get: ({ get }) => {
    const item = get(fundCategoriesFilterState);
    return item;
  },
});

export const fundDetailsQuery = selectorFamily<
  FundDetailsQueryData | null | undefined,
  { fundId: string; fundCnpj: string; accountNumber: string; screen?: string }
>({
  key: 'NewApplication/fundDetailsQuery',
  get: ({ fundId, fundCnpj, accountNumber, screen }) => {
    return async () => {
      if (!fundId || !fundCnpj) {
        return;
      }
      const { data } = await Service.Fund.V1.getOffersDetails({
        period: 3,
        checkingAccountNumber: accountNumber,
        fundCnpj,
        productId: fundId,
        screenName: screen || screenName,
      });

      const normalizedData = normalizeFundOfferDetail(data);

      return { data: normalizedData };
    };
  },
});

export const fundsOffersQueryUpdated = selectorFamily<
  {
    data: FundOfferEntityNormalized[];
    meta: MetaResponse;
    filters?: DynamicCustomFiltersProps['filters'];
  },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/fundsOffersQueryUpdated',
  get: ({
    accountNumber,
    checkingAccountNumber,
    accountId,
    filtersToApply,
    searchTerm,
    page,
    orderBy,
    fundsOffersV2Enabled,
  }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);
        const useV2 = !!fundsOffersV2Enabled && !!accountNumber;

        const params = {
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          ...(checkingAccountNumber ? { checkingAccountNumber } : {}),
          ...(accountNumber ? { accountNumber } : {}),
          ...(accountId ? { accountId } : {}),
          sortItems: sortItems && [sortItems],
          page,
          limit: 12,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          isExclusive: false,
          screenName,
        };

        if (useV2) {
          const { data, meta } = await Service.Fund.V2.getOffersFilter(params);
          const normalizedData = normalizeFundsOffersV2(data, accountId);
          const normalizedFilters = normalizeFundsFiltersFromMeta(meta);

          return { data: normalizedData, meta, filters: normalizedFilters };
        }

        const ServiceToCall = accountNumber
          ? Service.Fund.V1.getOffersFilter
          : Service.Fund.V1.getAllOffersBasket;

        const { data, meta } = await ServiceToCall(params);

        const normalizedData = normalizeFundsOffers(data);
        const normalizedFilters = normalizeFundsFiltersFromMeta(meta);

        return { data: normalizedData, meta, filters: normalizedFilters };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const fundsSellOffersQueryUpdated = selectorFamily<
  { data: FundRedemptionOfferNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/fundsOffersQueryUpdated',
  get: ({ accountNumber, searchTerm }) => {
    return async () => {
      try {
        if (!accountNumber) {
          return { data: [], meta: blankMetaResponse() };
        }

        const ServiceToCall = searchTerm
          ? Service.Fund.V1.getRedemptionsAccountNumberSerachTerm
          : Service.Fund.V1.getAllRedemptions;

        const { data } = await ServiceToCall({
          searchTerm: searchTerm || '',
          accountNumber,
          screenName,
        });

        const normalizedData = normalizeRedemptionsFund(data || []);

        return { data: normalizedData, meta: fakeMetaResponse(normalizedData.length) };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const fundsCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'NewApplication/fundsCustomFiltersQuery',
  get: ({ checkingAccountNumber }) => {
    return async () => {
      const ServiceToCall = checkingAccountNumber
        ? Service.Fund.V1.getOffersCategories
        : Service.Fund.V1.getOffersCategoriesAdvisors;

      const { data } = await ServiceToCall({
        screenName,
        ...(checkingAccountNumber ? { checkingAccountNumber } : {}),
      });

      const normalizedData = normalizeFundsFilters(data);

      return normalizedData;
    };
  },
});

export const updateFundOffersOperationTypeSelector = selector({
  key: 'updateFundOffersOperationTypeSelector',
  get: ({ get }) => {
    const item = get(fundOffersOperationTypeState);
    return item;
  },
  set: ({ set }, newValue) => {
    const data = newValue;
    return set(fundOffersOperationTypeState, data);
  },
});

export const fundRedemptionsOffersQuery = selector({
  key: 'NewApplication/fundRedemptionsOffersQuery',
  get: async ({ get }) => {
    try {
      const { accountNumber } = get(customerItemState).current;

      const { data } = await Service.Fund.V1.getAllRedemptions({
        screenName,
        accountNumber,
      });

      const normalizedData = normalizeRedemptionsFund(data || []);

      return { data: normalizedData };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [] };
      }

      throw error;
    }
  },
});

export const fundRedemptionsOffersBySearchTermQuery = selector({
  key: 'NewApplication/fundRedemptionsOffersBySearchTermQuery',
  get: async ({ get }) => {
    const fundRedemptionsBySearchTermParams = get(fundRedemptionsOffersBySearchTermParamsState);
    const { accountNumber } = get(customerItemState).current;

    if (fundRedemptionsBySearchTermParams.skipEndpointCall) {
      return;
    }

    const { data } = await Service.Fund.V1.getRedemptionsAccountNumberSerachTerm({
      ...fundRedemptionsBySearchTermParams,
      screenName,
      accountNumber,
    });

    const normalizedData = normalizeRedemptionsFund(data || []);

    return { data: normalizedData };
  },
});

export const updateFundRedemptionsOffersBySearchTermParamsSelector = selector({
  key: 'updateFundRedemptionsOffersBySearchTermParamsSelector',

  set: ({ set, get }, value) => {
    const data = {
      ...get(fundRedemptionsOffersBySearchTermParamsState),
      ...(value as Partial<FundRedemptionsOffersBySearchTerm>),
    };
    set(fundRedemptionsOffersBySearchTermParamsState, data);
  },
  get: ({ get }) => get(fundRedemptionsOffersBySearchTermParamsState),
});

export const updateFundRedemptionsOffersAllSelector = selector({
  key: 'updateFundRedemptionsOffersAllSelector',
  get: ({ get }) => get(fundRedemptionsOffersAllState),
  set: ({ set }, value) => set(fundRedemptionsOffersAllState, value),
});

export const updateFundRedemptionsOffersBySearchTermSelector = selector({
  key: 'updateFundRedemptionsOffersBySearchTermSelector',
  get: ({ get }) => get(fundRedemptionsOffersBySearchTermState),
  set: ({ set }, value) => set(fundRedemptionsOffersBySearchTermState, value),
});

export const updateFundRedemptionScheduleDateSelector = selector({
  key: 'updateFundRedemptionScheduleDateSelector',
  set: ({ set, get }, scheduleDate) => {
    const { current: currentProduct, previous } = get(productItemState);
    const data = {
      previous,
      current: { ...currentProduct, scheduleDate: scheduleDate as Date },
    };
    set(productItemState, data);
  },
  get: ({ get }) => get(productItemState),
});

export const updateFundRedemptionLiquidationAndQuotationDateSelector = selector({
  key: 'updateFundRedemptionLiquidationAndQuotationDateSelector',
  set: ({ set, get }, dates) => {
    const { current: currentProduct, previous } = get(productItemState);
    const data = {
      previous,
      current: { ...currentProduct, ...(dates as SelectedProduct) },
    };
    set(productItemState, data);
  },
  get: ({ get }) => get(productItemState),
});

export const fundComparatorProductsSearchSelector = selectorFamily<
  FundComparatorProductSearchResultNormalized[] | null | undefined,
  {
    keyword: string;
  }
>({
  key: 'fundComparator/productsSearchSelector',
  get:
    ({ keyword }) =>
    async ({ get }) => {
      if (!keyword) {
        return [];
      }

      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data } = await Service.Fund.V1.getAllOffersBasket({
          searchTerm: keyword,
          screenName,
          limit: 10,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        if (!data) {
          return [];
        }

        const normalizedData: FundComparatorProductSearchResultNormalized[] = data.map(
          ({ offer }) => ({
            id: offer.productId?.toString() ?? '',
            name: offer.productNameNormalized ?? '',
          })
        );

        return normalizedData;
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return [];
        }

        throw error;
      }
    },
});

export const fundComparatorProductsSelector = selectorFamily<
  FundComparatorResultNormalized,
  {
    productIds: (string | number)[];
    period: number;
  }
>({
  key: 'fundComparator/productsSelector',
  get:
    ({ productIds, period }) =>
    async () => {
      try {
        const response = await Service.Fund.V1.getProfitabilityComparison({
          ...(productIds.length > 0 && {
            productsId: productIds.map((id) => Number(id)),
          }),
          period: fundComparisonPeriod(period),
          screenName,
        });

        return normalizeFundComparison(response.data);
      } catch (error) {
        console.error('Error fetching fund comparison data:', error);
        throw error;
      }
    },
});
